/**
 *  Footer submenu arrows and collapsible widget titles. Sub-menus, and the
 *  menus under the titles on mobile, slide open and shut.
 *  Wrapped so its names stay local: the theme editor runs this file again
 *  each time it re-renders the footer.
 */
(function () {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animations = new WeakMap();

  // A closed menu takes no room: no height, and none of the bottom padding
  // (menus) or top margin (sub-menus) it has when open.
  const closed = { height: '0px', paddingBottom: '0px', marginTop: '0px' };

  // The panel's box as it stands, part-way through a slide or not.
  const measure = (panel) => {
    if (!panel.getClientRects().length) return closed;
    const style = getComputedStyle(panel);
    return {
      height: `${panel.offsetHeight}px`,
      paddingBottom: style.paddingBottom,
      marginTop: style.marginTop
    };
  };

  /**
   *  Runs `change`, which shows or hides the panel, and slides the panel
   *  from its old height to its new one, as CollapsibleRow does for
   *  <details>. Where `change` leaves the panel as it was (footer menus
   *  never close on desktop) nothing moves.
   */
  const slide = (panel, change) => {
    const from = measure(panel);
    if (animations.has(panel)) {
      animations.get(panel).cancel();
      animations.delete(panel);
      panel.style.display = '';
    }
    change();
    const to = measure(panel);
    if (from.height === to.height || reducedMotion.matches) return;

    // A closing panel is already hidden by its class: keep it on screen
    // until it has slid shut.
    panel.style.display = 'block';
    const animation = panel.animate([from, to], to === closed ?
      { duration: 250, easing: 'ease' } :
      { duration: 400, easing: 'ease-out' });
    animations.set(panel, animation);
    animation.onfinish = () => {
      animations.delete(panel);
      panel.style.display = '';
    };
  };

  /**
   *  @class
   *  @function ArrowSubMenu
   */
  class ArrowSubMenu {

    constructor(self) {
      this.item = self.parentNode;
      this.submenu = this.item.querySelector('.sub-menu');
      this.arrow = self;
      // Whether keyboard focus, rather than a click, opened the sub-menu
      this.openedByFocus = false;
      // Add functionality to buttons
      self.addEventListener('click', (e) => this.toggle_submenu(e));
      // Tabbing into the item opens its sub-menu and tabbing out closes it
      // again. Pointer focus is left alone: a click on the arrow focuses it
      // before the click itself would start the slide.
      this.item.addEventListener('focusin', (e) => this.focus_in(e));
      this.item.addEventListener('focusout', (e) => this.focus_out(e));
    }

    set_open(open) {
      slide(this.submenu, () => this.submenu.classList.toggle('active', open));
    }

    toggle_submenu(e) {
      e.preventDefault();
      this.openedByFocus = false;
      this.set_open(!this.submenu.classList.contains('active'));
    }

    focus_in(e) {
      if (this.submenu.classList.contains('active') || !e.target.matches(':focus-visible')) return;
      this.openedByFocus = true;
      this.set_open(true);
    }

    // A sub-menu that was clicked open stays open when focus moves on.
    focus_out(e) {
      if (!this.openedByFocus || this.item.contains(e.relatedTarget)) return;
      this.openedByFocus = false;
      this.set_open(false);
    }
  }

  /**
   *  @class
   *  @function FooterMenuToggle
   */
  class FooterMenuToggle {
    constructor() {
      document.querySelectorAll('.thb-widget-title.collapsible').forEach((button) => {
        if (button.dataset.footerBound) return;
        button.dataset.footerBound = 'true';
        const menu = button.nextElementSibling;
        button.addEventListener('click', () => {
          const toggle = () => button.classList.toggle('active');
          if (menu) {
            slide(menu, toggle);
          } else {
            toggle();
          }
        });
      });
    }
  }

  // Each element is bound once: a second run (a second footer, or the
  // editor re-rendering one) would otherwise toggle it twice per click.
  const init = () => {
    document.querySelectorAll('.thb-arrow').forEach((arrow) => {
      if (arrow.dataset.footerBound) return;
      arrow.dataset.footerBound = 'true';
      new ArrowSubMenu(arrow);
    });
    new FooterMenuToggle();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
